import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  ChevronRight,
  Circle,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import type { AnalyticsApi } from '../../api/analytics';
import type { AnalyticsGeoStatus, AnalyticsSite } from '../../api/types';
import { config } from '../../config';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { Spinner } from '../../components/Spinner';
import { type LoadState, useLoad } from '../../lib/useLoad';
import { useTimeFormat } from '../../lib/tenantTime';
import {
  type FunnelStep,
  type InstallSection,
  type LineProblem,
  RETENTION_CEILING_DAYS,
  appRivals,
  installState,
  ipProblems,
  linesToList,
  listToLines,
  moveStep,
  nextStepKey,
  originProblems,
  pathProblems,
  retentionChoices,
  setupChecklist,
  stepProblem,
  visitorKeyProblem,
  withUniqueKeys,
} from './installForm';
import {
  type FunnelTemplate,
  type StepChoice,
  choiceOf,
  choicesFor,
  installKind,
  reportsBookingSteps,
  templateSummary,
  templatesFor,
} from './funnelCatalog';
import { webSnippet } from './snippet';
import {
  Choice,
  CodeBlock,
  FlowStep,
  Group,
  Row,
  SaveBar,
  SectionHead,
  Switch,
} from '../../components/setup/SetupKit';

// Re-exported for the screens that took them from here before the kit existed.
export { Choice, SectionHead };

/**
 * The sections of an install's settings, one component each, and the few
 * pieces they are built from.
 *
 * Every settings form here holds a BASELINE — the install as the server last
 * answered for it — and is dirty while what it would send differs from what the
 * baseline would. The baseline is the PATCH's own answer rather than the list
 * re-read that follows it: that reload lands a beat later, and measuring
 * against the prop in the gap showed "unsaved changes" for a moment after every
 * save. Dirtiness is measured on the BODY, not the raw fields, so a trailing
 * space or a blank line in a list is not a change nobody can see.
 */

type GoTo = (section: InstallSection) => void;

/* ---- building blocks live in components/setup/SetupKit ---------------- */

function Problems({ list }: { list: readonly LineProblem[] }) {
  if (!list.length) return null;
  return (
    <ul className="an-set-problems">
      {list.map((problem, index) => (
        <li key={`${index}-${problem.line}`}>
          <code>{problem.line}</code> {problem.message}
        </li>
      ))}
    </ul>
  );
}

/**
 * A settings form's state against its baseline: the edits, whether they differ
 * from what was last saved, and the save itself.
 *
 * `of` reads a form out of an install and `body` turns a form into the PATCH;
 * both are module-level so their identity is stable.
 */
function useSiteForm<F>({
  api,
  site,
  section,
  of,
  body,
  onChanged,
  onDirty,
}: {
  api: AnalyticsApi;
  site: AnalyticsSite;
  section: InstallSection;
  of: (site: AnalyticsSite) => F;
  body: (form: F) => Partial<AnalyticsSite>;
  onChanged: () => void;
  onDirty: (section: InstallSection, dirty: boolean) => void;
}) {
  const [base, setBase] = useState(site);
  const [form, setForm] = useState(() => of(site));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // Problems show once a field is left or a save is tried, never mid-word: an
  // origin is "not a web address" for every keystroke until the last.
  const [attempted, setAttempted] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const dirty = useMemo(
    () => JSON.stringify(body(form)) !== JSON.stringify(body(of(base))),
    [body, of, form, base],
  );
  useEffect(() => onDirty(section, dirty), [onDirty, section, dirty]);

  const patch = (next: Partial<F>) => {
    setForm((was) => ({ ...was, ...next }));
    setSaved(false);
  };
  const touch = (field: string) => () => setTouched((was) => (was[field] ? was : { ...was, [field]: true }));
  const shows = (field: string) => attempted || Boolean(touched[field]);

  const discard = () => {
    setForm(of(base));
    setAttempted(false);
    setTouched({});
    setError(null);
  };

  const save = async (blocked: boolean) => {
    setAttempted(true);
    if (blocked) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const answer = await api.updateAnalyticsSite(site.id, body(form));
      // What the server KEPT, which is not always what was sent — an origin
      // comes back reduced to its origin, retention comes back clamped.
      setBase(answer.site);
      setForm(of(answer.site));
      setAttempted(false);
      setTouched({});
      setSaved(true);
      onChanged();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  return { form, patch, touch, shows, attempted, dirty, busy, error, saved, save, discard };
}

/* ---- Install ------------------------------------------------------------- */

// The booking's NUMBER — the Conf# on the confirmation, which is what a site
// built on the booking API has in hand. `jobUid` is still accepted, so a site
// already sending one keeps working; it is just no longer what we ask for.
const CONFIRMATION_LINE = [
  "// On your reservation-confirmation page, with the reservation's number (its Conf#):",
  "window.$sphereAnalytics.track('booking_confirmed', { jobId: 12345 });",
].join('\n');

const IDENTIFY_LINES = [
  '// After your customer signs in (Firebase JS SDK) — with your LimoSphere',
  '// customer login, or with a Firebase sign-in of your own:',
  'firebase.auth().onIdTokenChanged(async (user) => {',
  '  if (user) window.$sphereAnalytics.identify(await user.getIdToken());',
  '});',
].join('\n');

/** Steps 2 and 3, on the install the booking widgets report to: done for them there. */
const ON_WIDGET_PAGES = { label: 'Automatic on widget pages', tone: 'ok' } as const;


/**
 * Getting data in, and knowing that it is.
 *
 * Laid out as the order things are actually done in — paste, report bookings,
 * recognise customers — beside the two questions an operator brings back to
 * this screen: is it working, and what is left. Both of those are read off the
 * install itself rather than asserted, which is why the confirmation-page line
 * has no tick: nothing the console can see says whether a site sends it.
 *
 * `widgetLinked` is the install the dispatch dashboard's Website analytics
 * switch points the booking-widget pages at. The widgets report bookings and
 * sign-ins on those pages themselves, so steps 2 and 3 say so — and that they
 * are still the way to do it anywhere else. Every other install reads exactly
 * as it always has: a second site is a second install, set up by hand.
 *
 * An APP install has no steps at all. The passenger app carries the SDK in
 * every build and asks at launch which install it reports to
 * (services/siteIntegrations.js#appConfig), and a switched-on app install is
 * the answer — so setting it up is one switch, which IS the install's
 * Collecting. It writes the moment it is flipped rather than behind a Save: a
 * switch with nothing beside it has nothing to batch with. Collection does not
 * carry it for an app, or a stale copy there would switch the app back on with
 * the next unrelated save.
 */
export function SetupSection({
  api,
  site,
  sites,
  mayManage,
  widgetLinked = false,
  geoReady,
  refreshing,
  onCheck,
  onChanged,
  onGo,
}: {
  api: AnalyticsApi;
  site: AnalyticsSite;
  sites: readonly AnalyticsSite[];
  mayManage: boolean;
  widgetLinked?: boolean;
  geoReady: boolean | null;
  refreshing: boolean;
  onCheck: () => void;
  onChanged: () => void;
  onGo: GoTo;
}) {
  const id = useId();
  const { fmtDateTime } = useTimeFormat();
  const web = site.platform === 'web';
  // Only a website install can be the widgets' — an app has no pages to put them on.
  const widgets = web && widgetLinked;

  // What the app switch's write answered, held until the list re-read catches
  // up with it — otherwise the switch and the status beside it flick back to
  // the old answer for the beat between the two.
  const [held, setHeld] = useState<boolean | null>(null);
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  if (held !== null && held === site.enabled) setHeld(null);
  const current = held === null ? site : { ...site, enabled: held };

  const state = installState(current);
  const arrived = site.health !== 'never_reported';
  const rivals = appRivals(site, sites);

  const switchApp = async (enabled: boolean) => {
    setSwitching(true);
    setSwitchError(null);
    try {
      const answer = await api.updateAnalyticsSite(site.id, { enabled });
      setHeld(answer.site.enabled);
      onChanged();
    } catch (problem) {
      setSwitchError(problem instanceof Error ? problem.message : 'Could not switch it.');
    } finally {
      setSwitching(false);
    }
  };

  // The CONSOLE origin serves analytics.js, the SERVICE collects — two hosts,
  // both in the snippet. snippet.ts says what happens when the second is lost.
  const snippet =
    web && site.siteToken
      ? webSnippet({ siteToken: site.siteToken, crmBase: config.crmBase, consoleOrigin: window.location.origin })
      : null;
  const withheld = (
    <Note tone="info">
      The snippet carries a write credential, so it is only shown to someone who could change the install
      anyway. Ask an administrator for it.
    </Note>
  );

  const status = {
    ok: {
      title: 'Reporting',
      body: 'Data is arriving. Nothing to do here.',
    },
    waiting: {
      title: web ? 'Waiting for the first visit' : 'Waiting for the first launch',
      body: web
        ? 'Once the tag is on your site, open any page of it in a new tab, then press Check again. Visits usually show within a minute.'
        : 'Open the passenger app — close and reopen it if it was already running — and visit a screen or two, then press Check again.',
    },
    quiet: {
      title: 'Nothing in the last 24 hours',
      body: web
        ? 'On a quiet site that can simply mean nobody visited. Otherwise check the tag is still in your pages’ <head> — a redesign is the usual way it goes missing.'
        : 'On a quiet day that can simply mean nobody opened the app.',
    },
    off: web
      ? {
          title: 'Collecting is off',
          body: 'The tag is still answered, but nothing is recorded until collecting is switched back on.',
        }
      : {
          title: 'Switched off',
          body: 'The app stops reporting the next time it is opened, and nothing is recorded until this is switched back on.',
        },
  }[state];

  const items = mayManage ? setupChecklist(site, geoReady) : [];
  const doneCount = items.filter((item) => item.done).length;

  const rivalNames = <b>{rivals.map((rival) => rival.name).join(', ')}</b>;
  const toCollection = mayManage ? (
    <>
      {' '}
      <button type="button" className="an-link" onClick={() => onGo('collection')}>
        Open Collection
      </button>
    </>
  ) : null;

  return (
    <>
      <SectionHead title="Install">
        {web
          ? 'Put the tag on your site, then check that visits are arriving.'
          : 'Your passenger app already carries analytics. Switch it on here, then check that data is arriving.'}
      </SectionHead>

      {widgets ? (
        <Note tone="info">
          Your reservation widget pages report here — switched on in the dispatch dashboard under{' '}
          <b>My Website → Widgets</b>.
        </Note>
      ) : null}

      <div className="an-setup">
        {web ? (
          <ol className="an-flow">
            <FlowStep
              n={1}
              done={arrived}
              title="Add the tag to every page"
              tag={arrived ? { label: 'Done', tone: 'ok' } : { label: 'Start here', tone: 'accent' }}
            >
              <p>
                Paste this into the <code>&lt;head&gt;</code> of every page you want counted. It loads in
                the background and blocks nothing, and settings you change here reach it on each
                visitor’s next page load — you never paste it again.
              </p>
              {snippet ? <CodeBlock code={snippet} lang="HTML" label="the tag" primary={!arrived} /> : withheld}
            </FlowStep>
            <FlowStep
              n={2}
              title="Report each reservation"
              tag={widgets ? ON_WIDGET_PAGES : { label: 'Strongly recommended', tone: 'accent' }}
            >
              {widgets ? (
                <p>
                  Handled automatically on your reservation-widget pages: every reservation made in them is
                  reported, guest reservations included. You only need this line on other sites or pages
                  where customers book — on the confirmation page, with the reservation’s number (the Conf#
                  shown on the confirmation).
                </p>
              ) : (
                <p>
                  One line on your reservation-confirmation page, with the reservation’s number — the Conf# shown
                  on the confirmation. A visitor who books as a guest and never signs in cannot be tied
                  back to the visit that brought them any other way — and every reservation missed makes your
                  conversion figure read lower than it really is. A site already sending a{' '}
                  <code>jobUid</code> instead keeps working.
                </p>
              )}
              <CodeBlock code={CONFIRMATION_LINE} lang="JavaScript" label="the reservation line" />
            </FlowStep>
            <FlowStep
              n={3}
              title="Recognise signed-in customers"
              tag={widgets ? ON_WIDGET_PAGES : { label: 'Optional' }}
            >
              {widgets ? (
                <p>
                  Handled automatically on your reservation-widget pages: a customer who signs in there is
                  recognised. You only need these lines on other sites or pages, passing whichever sign-in
                  happened there — a LimoSphere customer sign-in, or your site’s own Firebase sign-in.
                </p>
              ) : (
                <p>
                  So a visit is tied to the customer and their reservations, rather than to an anonymous
                  visitor. The token can be a LimoSphere customer sign-in or your site’s own Firebase
                  sign-in.
                </p>
              )}
              <CodeBlock code={IDENTIFY_LINES} lang="JavaScript" label="the sign-in lines" />
            </FlowStep>
          </ol>
        ) : (
          <div>
            <Group>
              <Row
                label="Analytics in the passenger app"
                htmlFor={`${id}-app`}
                hint="Your LimoSphere passenger app asks every time it opens whether to report here, so there is nothing to paste and no app update to wait for. On, it starts at the next launch; off, it stops the same way. What has already been collected stays readable."
              >
                <Switch
                  id={`${id}-app`}
                  checked={current.enabled}
                  disabled={!mayManage || switching}
                  onChange={(enabled) => void switchApp(enabled)}
                />
              </Row>
            </Group>
            {rivals.length && current.enabled ? (
              <Note tone="warn">
                This install and {rivalNames} are {rivals.length === 1 ? 'both' : 'all'} switched on for the same
                app, so it reports to neither. Switch one off, or give each its own bundle identifier under
                Collection.{toCollection}
              </Note>
            ) : rivals.length ? (
              <Note tone="info">
                {rivalNames} {rivals.length === 1 ? 'is' : 'are'} already switched on for the same app. With two on
                at once it reports to neither — switch the other off first, or give each its own bundle identifier
                under Collection.{toCollection}
              </Note>
            ) : null}
            {switchError ? <ErrorNote error={switchError} /> : null}
          </div>
        )}

        <aside className="an-setup-aside">
          <section className={`an-status is-${state}`} aria-live="polite">
            <div className="an-status-title">
              <span className="an-status-dot" aria-hidden="true" />
              {status.title}
            </div>
            <p className="an-status-body">{status.body}</p>
            {site.lastEventAt ? (
              <p className="an-status-meta">Last data received {fmtDateTime(site.lastEventAt)}</p>
            ) : null}
            <div className="an-status-actions">
              <button type="button" className="btn btn-secondary" disabled={refreshing} onClick={onCheck}>
                <RefreshCw size={14} className={refreshing ? 'spin-anim' : undefined} />
                {refreshing ? 'Checking…' : 'Check again'}
              </button>
              {/* An app's switch is already beside this. */}
              {state === 'off' && mayManage && web ? (
                <button type="button" className="btn btn-ghost" onClick={() => onGo('collection')}>
                  Turn it on <ChevronRight size={14} />
                </button>
              ) : null}
            </div>
          </section>

          {items.length ? (
            <section className="an-checklist">
              <header className="an-checklist-head">
                <h3>Finish setting up</h3>
                <span>
                  {doneCount} of {items.length} done
                </span>
              </header>
              <div
                className="an-checklist-bar"
                role="progressbar"
                aria-label="Setup progress"
                aria-valuemin={0}
                aria-valuemax={items.length}
                aria-valuenow={doneCount}
              >
                <i style={{ width: `${(doneCount / items.length) * 100}%` }} />
              </div>
              <ul>
                {items.map((item) => (
                  <li key={item.key}>
                    <button
                      type="button"
                      className={item.done ? 'an-check is-done' : 'an-check'}
                      onClick={() => onGo(item.section)}
                    >
                      {item.done ? (
                        <CheckCircle2 size={16} className="an-check-icon" />
                      ) : (
                        <Circle size={16} className="an-check-icon" />
                      )}
                      <span className="an-check-text">
                        <span className="an-check-label">{item.label}</span>
                        <span className="an-check-detail">{item.detail}</span>
                      </span>
                      <ChevronRight size={14} className="an-check-go" />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </aside>
      </div>
    </>
  );
}

/* ---- Collection ------------------------------------------------------------ */

interface CollectionForm {
  web: boolean;
  enabled: boolean;
  origins: string;
  bundleId: string;
  excludePaths: string;
  excludeIps: string;
  searchParam: string;
  heatmap: boolean;
}

const collectionOf = (site: AnalyticsSite): CollectionForm => ({
  web: site.platform === 'web',
  enabled: site.enabled,
  origins: listToLines(site.allowedOrigins),
  bundleId: site.bundleId ?? '',
  excludePaths: listToLines(site.excludePaths),
  excludeIps: listToLines(site.excludeIps),
  searchParam: site.searchParam ?? '',
  heatmap: site.heatmapEnabled,
});

// An app has no origin, page path, search box or heatmap, so its PATCH names
// none of them — sending a web field for an app would store a value nothing
// reads, and the next person to open the form would wonder where it came from.
// Nor does it name `enabled`: an app's is the Install section's switch, and
// this form's copy goes stale the moment that is flipped — sent with the next
// save here, it would switch the app back on. The NAME is left out for the same
// reason on every install: it is renamed on the title (InstallManage), and a
// copy held here would put the old one back.
const collectionBody = (form: CollectionForm): Partial<AnalyticsSite> => ({
  excludeIps: linesToList(form.excludeIps),
  ...(form.web
    ? {
        enabled: form.enabled,
        allowedOrigins: linesToList(form.origins),
        excludePaths: linesToList(form.excludePaths),
        searchParam: form.searchParam.trim() || null,
        heatmapEnabled: form.heatmap,
      }
    : { bundleId: form.bundleId.trim() || null }),
});

export function CollectionSection({
  api,
  site,
  onChanged,
  onDirty,
}: {
  api: AnalyticsApi;
  site: AnalyticsSite;
  onChanged: () => void;
  onDirty: (section: InstallSection, dirty: boolean) => void;
}) {
  const id = useId();
  const f = useSiteForm({
    api,
    site,
    section: 'collection',
    of: collectionOf,
    body: collectionBody,
    onChanged,
    onDirty,
  });
  const { form, patch, touch, shows } = f;
  const web = form.web;

  const origins = web ? originProblems(linesToList(form.origins)) : [];
  const paths = web ? pathProblems(linesToList(form.excludePaths)) : [];
  const ips = ipProblems(linesToList(form.excludeIps));
  const blocked = origins.length + paths.length + ips.length > 0;

  return (
    <>
      <SectionHead title="Collection">
        What this install counts, where it accepts data from, and what it leaves out.
      </SectionHead>

      {web ? (
        <Group>
          <Row
            label="Collecting"
            htmlFor={`${id}-enabled`}
            hint="Off: the tag is still answered, but nothing is recorded. Reports you already have are untouched."
          >
            <Switch id={`${id}-enabled`} checked={form.enabled} onChange={(enabled) => patch({ enabled })} />
          </Row>
        </Group>
      ) : null}

      <Group
        title={web ? 'Where data may come from' : 'Which app reports here'}
        sub={
          web
            ? 'Data from anywhere else is refused, so nobody can fill your reports from their own pages.'
            : 'Usually left empty. It only matters with more than one app install, to keep them apart.'
        }
      >
        {web ? (
          <Row
            label="Allowed sites"
            htmlFor={`${id}-origins`}
            hint={
              <>
                One full address per line, such as <code>https://example.com</code>. The bare domain and{' '}
                <code>www.</code> are different addresses — list both if both serve your site. Leave it empty
                to accept any site: handy while you set up, worth narrowing afterwards.
              </>
            }
          >
            <textarea
              id={`${id}-origins`}
              className="textarea"
              rows={3}
              value={form.origins}
              placeholder={'https://example.com\nhttps://www.example.com'}
              aria-invalid={shows('origins') && origins.length ? true : undefined}
              onBlur={touch('origins')}
              onChange={(event) => patch({ origins: event.target.value })}
            />
            {shows('origins') ? <Problems list={origins} /> : null}
            {!linesToList(form.origins).length ? (
              <span className="an-set-flag">Any site is accepted right now.</span>
            ) : null}
          </Row>
        ) : (
          <Row
            label="Bundle identifier"
            htmlFor={`${id}-bundle`}
            hint="The app’s own id, e.g. com.example.passenger. An app reports to the install named with its id, or else to the one named with none. Leave it empty if your iOS and Android ids differ: only one can be named, and the other app would stop reporting."
          >
            <input
              id={`${id}-bundle`}
              className="input"
              value={form.bundleId}
              placeholder="com.example.passenger"
              onChange={(event) => patch({ bundleId: event.target.value })}
            />
          </Row>
        )}
      </Group>

      <Group title="What to leave out" sub="Excluded visits are never stored, so they cannot be shown later.">
        {web ? (
          <Row
            label="Ignore these pages"
            htmlFor={`${id}-paths`}
            hint={
              <>
                One path per line. <code>*</code> matches the rest, so <code>/admin/*</code> covers everything
                beneath it.
              </>
            }
          >
            <textarea
              id={`${id}-paths`}
              className="textarea"
              rows={3}
              value={form.excludePaths}
              placeholder="/admin/*"
              aria-invalid={shows('paths') && paths.length ? true : undefined}
              onBlur={touch('paths')}
              onChange={(event) => patch({ excludePaths: event.target.value })}
            />
            {shows('paths') ? <Problems list={paths} /> : null}
          </Row>
        ) : null}
        <Row
          label="Ignore these addresses"
          htmlFor={`${id}-ips`}
          hint="One IP address per line — your office, so your team’s own browsing does not count as traffic. Each address exactly: ranges are not matched."
        >
          <textarea
            id={`${id}-ips`}
            className="textarea"
            rows={3}
            value={form.excludeIps}
            placeholder="203.0.113.14"
            aria-invalid={shows('ips') && ips.length ? true : undefined}
            onBlur={touch('ips')}
            onChange={(event) => patch({ excludeIps: event.target.value })}
          />
          {shows('ips') ? <Problems list={ips} /> : null}
        </Row>
      </Group>

      {web ? (
        <Group title="Extra reports" sub="Both are off until you turn them on.">
          <Row
            label="Site search"
            htmlFor={`${id}-search`}
            hint={
              <>
                If your search results page carries the term in its address, like <code>/search?q=airport</code>,
                name the parameter — <code>q</code> there — and searches become a report. It is the one query
                parameter kept from an address; the rest are dropped before storage, because that is where tokens
                and email addresses live. People type personal details into search boxes, so leaving this empty
                is a fine choice.
              </>
            }
          >
            <input
              id={`${id}-search`}
              className="input an-input-short"
              value={form.searchParam}
              placeholder="q"
              onChange={(event) => patch({ searchParam: event.target.value })}
            />
          </Row>
          <Row
            label="Clicks and scrolling"
            htmlFor={`${id}-heat`}
            hint="Records where people click and how far they scroll, for the Clicks report. Its storage grows with the size of your pages rather than your traffic — turn it on for a few weeks, read it, then turn it off."
          >
            <Switch id={`${id}-heat`} checked={form.heatmap} onChange={(heatmap) => patch({ heatmap })} />
          </Row>
        </Group>
      ) : null}

      {f.error ? <ErrorNote error={f.error} /> : null}
      <SaveBar
        dirty={f.dirty}
        busy={f.busy}
        saved={f.saved}
        blocked={f.dirty && f.attempted && blocked}
        savedNote={web ? 'Saved. Visitors pick it up on their next page load.' : 'Saved.'}
        onSave={() => void f.save(blocked)}
        onDiscard={f.discard}
      />
    </>
  );
}

/* ---- Booking funnel ------------------------------------------------------- */

interface FunnelForm {
  steps: FunnelStep[];
  leadStep: string;
  abandonAfter: string;
}

const funnelOf = (site: AnalyticsSite): FunnelForm => ({
  steps: withUniqueKeys(
    (site.funnel ?? []).map((step) => ({
      key: step.key,
      label: step.label,
      match: { type: step.match?.type ?? 'path', value: step.match?.value ?? '' },
    })),
  ),
  leadStep: site.leadStep ?? '',
  abandonAfter: String(site.abandonAfterMinutes ?? 45),
});

const funnelBody = (form: FunnelForm): Partial<AnalyticsSite> => ({
  funnel: form.steps.map((step) => ({
    key: step.key,
    label: step.label.trim(),
    match: { type: step.match?.type ?? 'path', value: (step.match?.value ?? '').trim() },
  })),
  leadStep: form.leadStep || null,
  abandonAfterMinutes: Number(form.abandonAfter),
});

/** How far back "seen on this install" reaches — the raw tier's whole window. */
const SEEN_DAYS = 90;
const daysAgo = (days: number) => new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);

/** A select is as wide as its longest option, and a stored path can run to 512 characters. */
const shortPath = (path: string) => (path.length > 48 ? `${path.slice(0, 47)}…` : path);

const CUSTOM = { path: 'custom:path', event: 'custom:event' } as const;
const choiceKey = (choice: Pick<StepChoice, 'type' | 'value'>) => `${choice.type}:${choice.value}`;

/**
 * The funnel, built from a list rather than typed — for the app and the booking
 * widgets the list is what they actually report (funnelCatalog.ts), and for a
 * website of the tenant's own it is the pages it has been seen reporting. Typing
 * a page or an event by hand is still one choice away, and a step saved before
 * the list existed simply opens as typed.
 *
 * `widgetLinked` is the same fact the Install section reads: the install the
 * booking-widget pages report to, whose events are the widgets' two.
 */
export function FunnelSection({
  api,
  site,
  widgetLinked = false,
  onChanged,
  onDirty,
}: {
  api: AnalyticsApi;
  site: AnalyticsSite;
  widgetLinked?: boolean;
  onChanged: () => void;
  onDirty: (section: InstallSection, dirty: boolean) => void;
}) {
  const id = useId();
  const f = useSiteForm({
    api,
    site,
    section: 'funnel',
    of: funnelOf,
    body: funnelBody,
    onChanged,
    onDirty,
  });
  const { form, patch, touch, shows } = f;
  const app = site.platform === 'app';
  const kind = installKind(site.platform, widgetLinked);

  // What this install has reported, busiest first. A read that fails costs only
  // the suggestions: the known list and typing by hand still work.
  const seen = useLoad(
    () => api.getAnalyticsTop({ siteId: site.id, from: daysAgo(SEEN_DAYS) }, 'page', 50),
    [site.id],
  );
  const seenPaths = useMemo(() => (seen.data?.payload ?? []).map((row) => row.value), [seen.data]);
  const choices = useMemo(() => choicesFor(kind, seenPaths), [kind, seenPaths]);
  const templates = useMemo(() => templatesFor(kind, seenPaths), [kind, seenPaths]);
  const pages = [...choices.known, ...choices.seen].filter((choice) => choice.type === 'path');
  const events = choices.known.filter((choice) => choice.type === 'event');

  // Steps the operator chose to type by hand. Kept even when what they type turns
  // out to be on the list, or the box would vanish under them mid-word.
  const [typed, setTyped] = useState<Record<string, boolean>>({});
  const [offered, setOffered] = useState<FunnelTemplate | null>(null);

  /** '' for a step nothing has been chosen for yet, a choice's key, or a CUSTOM key. */
  const modeOf = (step: FunnelStep): { key: string; choice: StepChoice | null } => {
    const choice = typed[step.key] ? null : choiceOf(step, choices);
    if (choice) return { key: choiceKey(choice), choice };
    if (!typed[step.key] && !(step.match?.value ?? '')) return { key: '', choice: null };
    return { key: CUSTOM[step.match?.type ?? 'path'], choice: null };
  };

  const edit = (index: number, next: Partial<FunnelStep>) =>
    patch({ steps: form.steps.map((step, i) => (i === index ? { ...step, ...next } : step)) });
  const remove = (index: number) => {
    const gone = form.steps[index];
    patch({
      steps: form.steps.filter((_, i) => i !== index),
      // A lead step that no longer exists would be saved as a pointer to nothing.
      ...(gone && gone.key === form.leadStep ? { leadStep: '' } : {}),
    });
  };

  const choose = (index: number, step: FunnelStep, key: string) => {
    if (key === CUSTOM.path || key === CUSTOM.event) {
      const type = key === CUSTOM.path ? 'path' : 'event';
      setTyped((was) => ({ ...was, [step.key]: true }));
      // Same kind as before: keep what it was, as a starting point to edit.
      edit(index, { match: { type, value: step.match?.type === type ? (step.match?.value ?? '') : '' } });
      return;
    }
    const choice = [...choices.known, ...choices.seen].find((entry) => choiceKey(entry) === key);
    if (!choice) return;
    const was = modeOf(step).choice;
    setTyped(({ [step.key]: _dropped, ...rest }) => rest);
    edit(index, {
      match: { type: choice.type, value: choice.value },
      // A name the operator wrote is theirs; an empty one, or the last choice's
      // own, follows the choice.
      label: !step.label.trim() || step.label === was?.label ? choice.label : step.label,
    });
  };

  const apply = (template: FunnelTemplate) => {
    setTyped({});
    setOffered(null);
    patch({
      steps: template.steps.map((step) => ({ ...step, match: step.match ? { ...step.match } : undefined })),
      leadStep: template.leadStep ?? '',
    });
  };

  const problems = form.steps.map((step) =>
    modeOf(step).key === '' ? 'Choose what reaches this step.' : stepProblem(step),
  );
  const minutes = Number(form.abandonAfter);
  const minutesProblem =
    Number.isInteger(minutes) && minutes >= 5 && minutes <= 1440 ? null : 'Between 5 and 1440 minutes.';
  const blocked = problems.some(Boolean) || Boolean(minutesProblem);

  return (
    <>
      <SectionHead title="Reservation funnel">
        The steps a visitor passes through on the way to a reservation. The funnel card counts how many reach each
        one, and where the rest stop.
      </SectionHead>

      <Group
        title="Steps, in order"
        sub={
          app ? (
            <>
              Pick a screen of the app, or something it reports. A screen step also counts every screen whose name{' '}
              <b>starts with</b> it.
            </>
          ) : (
            <>
              Pick a page or something your site reports. A page step counts every address that <b>starts with</b>{' '}
              it — so <code>/book</code> also covers <code>/book/details</code>.
            </>
          )
        }
      >
        <div className="an-funnel-wrap">
          <div className="an-set-actions">
            <span className="an-set-muted">Start from a ready-made funnel:</span>
            {templates.map((template) => (
              <button
                key={template.key}
                type="button"
                className="btn btn-secondary"
                title={templateSummary(template)}
                onClick={() => (form.steps.length ? setOffered(template) : apply(template))}
              >
                {template.title}
              </button>
            ))}
          </div>
          {form.steps.length ? (
            <ol className="an-funnel">
              {form.steps.map((step, index) => {
                const problem = shows(`step-${index}`) ? problems[index] : null;
                const type = step.match?.type ?? 'path';
                const mode = modeOf(step);
                const custom = mode.key === CUSTOM.path || mode.key === CUSTOM.event;
                return (
                  <li className="an-funnel-step" key={step.key}>
                    <span className="an-funnel-num" aria-hidden="true">
                      {index + 1}
                    </span>
                    <div className="an-funnel-fields" onBlur={touch(`step-${index}`)}>
                      <input
                        className="input"
                        value={step.label}
                        placeholder={app ? 'Opened the reservation form' : 'Opened the reservation page'}
                        aria-label={`Step ${index + 1} name`}
                        aria-invalid={problem ? true : undefined}
                        onChange={(event) => edit(index, { label: event.target.value })}
                      />
                      <select
                        className="select"
                        value={mode.key}
                        aria-label={`Step ${index + 1} is reached when`}
                        aria-invalid={problem && mode.key === '' ? true : undefined}
                        onChange={(event) => choose(index, step, event.target.value)}
                      >
                        {mode.key === '' ? (
                          <option value="" disabled>
                            Choose…
                          </option>
                        ) : null}
                        <optgroup label={app ? 'Screens' : 'Pages'}>
                          {pages.map((choice) => (
                            <option key={choiceKey(choice)} value={choiceKey(choice)}>
                              {shortPath(choice.label)}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="Events">
                          {events.map((choice) => (
                            <option key={choiceKey(choice)} value={choiceKey(choice)}>
                              {choice.label}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="Something else">
                          <option value={CUSTOM.path}>{app ? 'A screen starting with…' : 'A page starting with…'}</option>
                          <option value={CUSTOM.event}>An event named…</option>
                        </optgroup>
                      </select>
                      {custom ? (
                        <input
                          className="input an-mono"
                          value={step.match?.value ?? ''}
                          placeholder={type === 'event' ? 'quote_requested' : app ? '/book-now' : '/book/details'}
                          aria-label={
                            type === 'event'
                              ? `Step ${index + 1} event name`
                              : `Step ${index + 1} ${app ? 'screen name' : 'page address'}`
                          }
                          aria-invalid={problem ? true : undefined}
                          onChange={(event) => edit(index, { match: { type, value: event.target.value } })}
                        />
                      ) : (
                        <span className="an-set-muted">
                          {mode.choice && mode.choice.label !== mode.choice.value ? (
                            <code>{mode.choice.value}</code>
                          ) : null}
                          {mode.choice?.note ? ` · ${mode.choice.note}` : null}
                        </span>
                      )}
                      {problem ? <span className="field-error an-funnel-problem">{problem}</span> : null}
                    </div>
                    <div className="an-funnel-tools">
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon"
                        aria-label={`Move step ${index + 1} up`}
                        disabled={index === 0}
                        onClick={() => patch({ steps: moveStep(form.steps, index, index - 1) })}
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon"
                        aria-label={`Move step ${index + 1} down`}
                        disabled={index === form.steps.length - 1}
                        onClick={() => patch({ steps: moveStep(form.steps, index, index + 1) })}
                      >
                        <ArrowDown size={14} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-icon"
                        aria-label={`Remove step ${index + 1}`}
                        onClick={() => remove(index)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ol>
          ) : (
            <p className="an-funnel-empty">
              No steps yet. Start from a ready-made funnel above, or add the steps one at a time.
            </p>
          )}
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() =>
              patch({
                steps: [...form.steps, { key: nextStepKey(form.steps), label: '', match: { type: 'path', value: '' } }],
              })
            }
          >
            <Plus size={14} /> Add a step
          </button>
        </div>
      </Group>

      {/* What the list cannot offer, said where the operator is building the funnel
          rather than discovered as a card that never shows where people stop. */}
      {kind === 'app' && !reportsBookingSteps(seenPaths) ? (
        <Note tone="info">
          No step of the reservation form has been reported here yet, so to this funnel the form is one screen: it can
          show who opened it and who booked, but not the step where the rest stopped. Each step — trip, vehicle,
          details, summary and payment — appears in the list, with a step-by-step funnel, once customers use a
          version of the app that reports them.
        </Note>
      ) : kind === 'widgets' ? (
        <Note tone="info">
          Your reservation widgets report a reservation and a quote request, and nothing in between — no steps, and no page
          of their own, since they sit inside yours. So a funnel can show who reached the page with the widget and who
          booked, but not the step where the rest stopped.
        </Note>
      ) : null}

      <Group title="Leads" sub="Who your team is told about when a reservation is not finished.">
        <Row
          label="Which step counts as an enquiry"
          htmlFor={`${id}-lead`}
          hint="A visitor who reached this step, signed in, and then left without booking is raised to your team as a lead worth following up."
        >
          <select
            id={`${id}-lead`}
            className="select"
            value={form.leadStep}
            onChange={(event) => patch({ leadStep: event.target.value })}
          >
            <option value="">Do not raise leads</option>
            {form.steps.map((step, index) => (
              <option key={step.key} value={step.key}>
                {step.label.trim() || `Step ${index + 1} (unnamed)`}
              </option>
            ))}
          </select>
        </Row>
        <Row
          label="Give up on them after"
          htmlFor={`${id}-abandon`}
          hint="A visit only counts as abandoned once this long has passed with nothing more from them, so someone who steps away from their desk mid-booking is not chased."
        >
          <div className="an-input-unit">
            <input
              id={`${id}-abandon`}
              className="input an-input-short"
              type="number"
              min={5}
              max={1440}
              value={form.abandonAfter}
              aria-invalid={shows('abandon') && minutesProblem ? true : undefined}
              onBlur={touch('abandon')}
              onChange={(event) => patch({ abandonAfter: event.target.value })}
            />
            <span>minutes</span>
          </div>
          {shows('abandon') && minutesProblem ? <span className="field-error">{minutesProblem}</span> : null}
        </Row>
      </Group>

      {f.error ? <ErrorNote error={f.error} /> : null}
      <SaveBar
        dirty={f.dirty}
        busy={f.busy}
        saved={f.saved}
        blocked={f.dirty && f.attempted && blocked}
        savedNote="Saved. The funnel card reads it from now on."
        onSave={() => void f.save(blocked)}
        onDiscard={f.discard}
      />

      {offered ? (
        <ConfirmDialog
          title={`Replace your steps with ${offered.title}?`}
          body={
            <p>
              The {form.steps.length === 1 ? 'step' : `${form.steps.length} steps`} here become{' '}
              <b>{templateSummary(offered)}</b>. Nothing is saved until you press Save changes, and Discard puts
              back what was saved before.
            </p>
          }
          confirmLabel="Replace steps"
          onConfirm={() => apply(offered)}
          onCancel={() => setOffered(null)}
        />
      ) : null}
    </>
  );
}

/* ---- Privacy -------------------------------------------------------------- */

interface PrivacyForm {
  mode: 'persistent' | 'cookieless';
  respectDnt: boolean;
  retention: number;
}

const privacyOf = (site: AnalyticsSite): PrivacyForm => ({
  mode: site.trackingMode === 'cookieless' ? 'cookieless' : 'persistent',
  respectDnt: site.respectDnt,
  retention: Math.min(RETENTION_CEILING_DAYS, site.retentionDays || RETENTION_CEILING_DAYS),
});

const privacyBody = (form: PrivacyForm): Partial<AnalyticsSite> => ({
  trackingMode: form.mode,
  respectDnt: form.respectDnt,
  retentionDays: form.retention,
});

export function PrivacySection({
  api,
  site,
  onChanged,
  onDirty,
  onGo,
}: {
  api: AnalyticsApi;
  site: AnalyticsSite;
  onChanged: () => void;
  onDirty: (section: InstallSection, dirty: boolean) => void;
  onGo: GoTo;
}) {
  const id = useId();
  const f = useSiteForm({
    api,
    site,
    section: 'privacy',
    of: privacyOf,
    body: privacyBody,
    onChanged,
    onDirty,
  });
  const { form, patch } = f;

  return (
    <>
      <SectionHead title="Privacy">
        How visitors are recognised, who is not counted at all, and how long the detail is kept.
      </SectionHead>

      <Group title="How visitors are recognised">
        <div className="an-choices">
          <Choice
            name={`${id}-mode`}
            value="persistent"
            current={form.mode}
            onChange={(mode) => patch({ mode })}
            title="Recognise returning visitors"
          >
            A random id is kept in the visitor’s browser, so someone who comes back next week counts as returning
            rather than new.
          </Choice>
          <Choice
            name={`${id}-mode`}
            value="cookieless"
            current={form.mode}
            onChange={(mode) => patch({ mode })}
            title="Cookieless"
          >
            Nothing is stored on the visitor’s device. Each person is recognised for one day at a time from a
            one-way fingerprint thrown away at midnight — in many places that removes the need for a consent
            banner. The cost: someone who comes back tomorrow counts as new, so returning visitors read low.
          </Choice>
        </div>
      </Group>

      <Group>
        <Row
          label="Honour Do Not Track and Global Privacy Control"
          htmlFor={`${id}-dnt`}
          hint="On by default. A browser asking not to be tracked is not counted at all — no identifier, nothing written to the device, no request sent."
        >
          <Switch id={`${id}-dnt`} checked={form.respectDnt} onChange={(respectDnt) => patch({ respectDnt })} />
        </Row>
        <Row
          label="Keep the detail of each visit for"
          htmlFor={`${id}-retention`}
          hint="Individual visits, pages and events are deleted after this — 90 days at most. The daily totals behind every chart are kept for three years either way, so your history and year-on-year comparisons survive. The address a visit came from is never stored, and its one-way scramble is erased after seven days regardless."
        >
          <select
            id={`${id}-retention`}
            className="select an-input-short"
            value={form.retention}
            onChange={(event) => patch({ retention: Number(event.target.value) })}
          >
            {retentionChoices(form.retention).map((days) => (
              <option key={days} value={days}>
                {days} days
              </option>
            ))}
          </select>
        </Row>
      </Group>

      <Note tone="info">
        You are the data controller for what this collects; we process it for you. A consent banner and a line in
        your privacy policy are your decisions to make — and erasing one person on request is a single action under{' '}
        <button type="button" className="an-link" onClick={() => onGo('data')}>
          Data &amp; removal
        </button>
        .
      </Note>

      {f.error ? <ErrorNote error={f.error} /> : null}
      <SaveBar
        dirty={f.dirty}
        busy={f.busy}
        saved={f.saved}
        blocked={false}
        savedNote="Saved. Visitors pick it up on their next page load."
        onSave={() => void f.save(false)}
        onDiscard={f.discard}
      />
    </>
  );
}

/* ---- Data & removal -------------------------------------------------------- */

export function DataSection({
  api,
  site,
  canDelete,
  onRemoved,
  onGo,
}: {
  api: AnalyticsApi;
  site: AnalyticsSite;
  canDelete: boolean;
  onRemoved: () => void;
  onGo: GoTo;
}) {
  const id = useId();
  const [visitorKey, setVisitorKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [erased, setErased] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<'erase' | 'remove' | null>(null);
  const [triedErase, setTriedErase] = useState(false);

  const key = visitorKey.trim();
  const keyProblem = key ? visitorKeyProblem(key) : null;

  const erase = async () => {
    setBusy(true);
    setError(null);
    setErased(null);
    try {
      await api.purgeAnalyticsVisitor(site.id, key);
      setErased(key);
      setVisitorKey('');
      setTriedErase(false);
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'Could not erase that visitor.');
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.deleteAnalyticsSite(site.id);
      setConfirming(null);
      onRemoved();
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'Could not remove the install.');
      setConfirming(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SectionHead title="Data & removal">
        Erasing what is held about one person, and removing this install altogether. Neither can be undone.
      </SectionHead>

      {error ? <ErrorNote error={error} /> : null}

      <Group
        title="Erase one visitor"
        sub="For when someone asks you to delete what you hold about them."
      >
        <div className="an-set-pad">
          <ol className="an-howto">
            <li>
              Find one of their visits — under <b>Recent visits</b> on the dashboard — and open it.
            </li>
            <li>
              Copy the <b>visitor id</b> at the foot of the visit. It starts with <code>av_</code>.
            </li>
            <li>Paste it here. Everything recorded about that person is deleted outright.</li>
          </ol>
          {erased ? (
            <Note tone="ok">
              <code>{erased}</code> and everything recorded about them are gone. The totals they contributed to are
              recalculated without them on the next nightly pass.
            </Note>
          ) : null}
          <div className="an-inline-form">
            <label className="an-set-label" htmlFor={`${id}-visitor`}>
              Visitor id
            </label>
            <div className="an-inline-form-row">
              <input
                id={`${id}-visitor`}
                className="input an-mono"
                value={visitorKey}
                placeholder="av_…"
                aria-invalid={triedErase && keyProblem ? true : undefined}
                onBlur={() => key && setTriedErase(true)}
                onChange={(event) => {
                  setVisitorKey(event.target.value);
                  setErased(null);
                }}
              />
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy || !key}
                onClick={() => {
                  setTriedErase(true);
                  if (!keyProblem) setConfirming('erase');
                }}
              >
                Erase visitor…
              </button>
            </div>
            {triedErase && keyProblem ? <span className="field-error">{keyProblem}</span> : null}
          </div>
        </div>
      </Group>

      <Group title="Remove this install" danger>
        <div className="an-set-pad an-danger-row">
          {canDelete ? (
            <>
              <p>
                Deletes every visit, report and total <b>{site.name}</b> ever collected.{' '}
                {site.platform === 'app'
                  ? 'The app then stops reporting at its next launch.'
                  : 'The tag on your pages then does nothing.'}{' '}
                There is no undo and no export afterwards.
              </p>
              <button type="button" className="btn btn-danger" disabled={busy} onClick={() => setConfirming('remove')}>
                <Trash2 size={14} /> Remove install…
              </button>
            </>
          ) : (
            <>
              <p>
                This is your only install, so it cannot be removed. To stop it recording,{' '}
                {site.platform === 'app' ? 'switch it off under Install' : 'switch Collecting off'} instead — what
                it has already collected stays readable.
              </p>
              {site.platform === 'app' ? (
                <button type="button" className="btn btn-secondary" onClick={() => onGo('install')}>
                  Open Install <ChevronRight size={14} />
                </button>
              ) : (
                <button type="button" className="btn btn-secondary" onClick={() => onGo('collection')}>
                  Open Collection <ChevronRight size={14} />
                </button>
              )}
            </>
          )}
        </div>
      </Group>

      {confirming === 'erase' ? (
        <ConfirmDialog
          title="Erase this visitor?"
          body={
            <p>
              Everything recorded about <code>{key}</code> on <b>{site.name}</b> — every visit, page and event — is
              deleted. It cannot be brought back.
            </p>
          }
          confirmLabel="Erase visitor"
          danger
          busy={busy}
          onConfirm={() => void erase()}
          onCancel={() => setConfirming(null)}
        />
      ) : null}
      {confirming === 'remove' ? (
        <ConfirmDialog
          title="Remove this install?"
          body={
            <p>
              Every visit, report and total collected for <b>{site.name}</b> is deleted. There is no undo and no
              export afterwards.
            </p>
          }
          confirmLabel="Remove install"
          danger
          requireText={site.name}
          busy={busy}
          onConfirm={() => void remove()}
          onCancel={() => setConfirming(null)}
        />
      ) : null}
    </>
  );
}

/* ---- Location data ---------------------------------------------------------- */

/**
 * Where town and region come from — the one setting here that is not this
 * install's.
 *
 * MaxMind gives GeoLite2 away to anyone with a free account, and this platform
 * ships no key of its own, so the operator supplies theirs. The database is a
 * single file per process, shared by every install on this CRM, which is why
 * the control is root's, why it sits under its own heading in the rail, and why
 * the section says so in as many words: a key set here switches town and region
 * on for everyone, and clearing it takes them away from everyone. Without one
 * nothing breaks — country still resolves from Cloudflare's header.
 */
export function LocationSection({ api, status }: { api: AnalyticsApi; status: LoadState<AnalyticsGeoStatus> }) {
  const id = useId();
  const { fmtDateTime } = useTimeFormat();
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [saved, setSaved] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => (pollRef.current ? clearTimeout(pollRef.current) : undefined), []);

  const geo = status.data;
  const ready = geo?.database === 'ready';

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await work();
      status.reload();
      setSaved(true);
    } catch (problem) {
      setError(problem as Error);
    } finally {
      setBusy(false);
    }
  };

  /*
   * The download is enqueued rather than awaited by the server — an archive of
   * tens of megabytes is not something to hold a request open for — so the
   * outcome arrives by re-reading the status. Polled for a minute and then let
   * go: a job that has not landed by then is one the operator will see the
   * result of when they next open the screen, not one worth spinning forever.
   */
  const download = () =>
    run(async () => {
      await api.refreshAnalyticsGeo();
      setDownloading(true);
      const started = Date.now();
      const tick = () => {
        pollRef.current = setTimeout(async () => {
          const next = await api.getAnalyticsGeo().catch(() => null);
          const landed = next?.lastRefresh?.at && next.lastRefresh.at !== geo?.lastRefresh?.at;
          if (landed || Date.now() - started > 60_000) {
            setDownloading(false);
            status.reload();
            return;
          }
          tick();
        }, 3000);
      };
      tick();
    });

  return (
    <>
      <SectionHead title="Location data">
        Which town and region a visit came from. Set once for this whole CRM — it applies to every install, not only
        this one.
      </SectionHead>

      {status.loading && !geo ? <Spinner /> : null}
      {status.error ? <ErrorNote error={status.error} /> : null}

      {geo ? (
        <>
          <Group>
            <div className="an-set-pad an-geo-state">
              <div className={ready ? 'an-geo-pill is-ready' : 'an-geo-pill'}>
                <span aria-hidden="true" />
                {ready ? 'Town and region are available' : 'Country only'}
              </div>
              <p>
                {ready
                  ? 'Visits resolve to a town and region on every install on this CRM.'
                  : 'No location database is loaded, so visits resolve to a country and no further. Nothing else is affected — the map and the country list work as they are.'}
                {geo.keySource === 'env' ? ' The key comes from this deployment’s configuration.' : null}
              </p>
              {geo.lastRefresh ? (
                <p className="an-geo-meta">
                  Last download {fmtDateTime(geo.lastRefresh.at)} —{' '}
                  {geo.lastRefresh.refreshed ? 'succeeded' : geo.lastRefresh.reason || 'failed'}
                </p>
              ) : null}
            </div>
          </Group>

          {geo.mayManage ? (
            <Group
              title="MaxMind licence key"
              sub={
                <>
                  Town and region come from MaxMind’s free GeoLite2 database, which needs a key from your own MaxMind
                  account —{' '}
                  <a href="https://www.maxmind.com/en/geolite2/signup" target="_blank" rel="noreferrer">
                    signing up
                  </a>{' '}
                  is free and takes a minute.
                </>
              }
            >
              <Row
                label="Licence key"
                htmlFor={`${id}-key`}
                hint="Stored encrypted and never shown again once saved, so this box stays empty even when a key is set. Paste a new one to replace it."
              >
                <input
                  id={`${id}-key`}
                  className="input"
                  type="password"
                  autoComplete="off"
                  value={key}
                  placeholder={geo.keySource === 'stored' ? '•••••••• (a key is stored)' : ''}
                  onChange={(event) => {
                    setKey(event.target.value);
                    setSaved(false);
                  }}
                />
                <div className="an-set-actions">
                  {/* An empty box saved REMOVES a stored key, so the button
                      says so rather than wearing "Save" over a deletion. A key
                      from the environment cannot be removed from here at all. */}
                  <button
                    type="button"
                    className={!key.trim() && geo.keySource === 'stored' ? 'btn btn-danger' : 'btn btn-primary'}
                    disabled={busy || (!key.trim() && geo.keySource !== 'stored')}
                    onClick={() =>
                      void run(async () => {
                        await api.saveAnalyticsGeoKey(key.trim());
                        setKey('');
                      })
                    }
                  >
                    {busy && !downloading ? <Spinner /> : null}
                    {!key.trim() && geo.keySource === 'stored' ? 'Remove key' : 'Save key'}
                  </button>
                  {saved && !downloading ? (
                    <span className="an-set-ok">
                      <CheckCircle2 size={14} /> Saved
                    </span>
                  ) : null}
                </div>
              </Row>
              <Row
                label="Database"
                hint="Refreshes itself weekly once a key is saved. MaxMind re-publishes GeoLite2 every Tuesday, and an out-of-date copy degrades slowly rather than breaking."
              >
                <div className="an-set-actions">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={busy || downloading || !geo.keySource}
                    title={geo.keySource ? undefined : 'Save a licence key first'}
                    onClick={() => void download()}
                  >
                    {downloading ? <Spinner /> : null}
                    {downloading ? 'Downloading…' : 'Download now'}
                  </button>
                  {!geo.keySource ? <span className="an-set-muted">Save a licence key first.</span> : null}
                </div>
              </Row>
              {error ? (
                <div className="an-set-pad">
                  <ErrorNote error={error} />
                </div>
              ) : null}
            </Group>
          ) : (
            <Note tone="info">
              The licence key is set once for this whole CRM, so only an administrator can change it.
            </Note>
          )}
        </>
      ) : null}
    </>
  );
}
